import { ReactNode } from 'react';
import { FilterVariantProps } from '@silverfort/sf-components/dist/Dropdown/interfaces';

export type ServiceAccountAction = 'log' | 'block' | 'siem';

export interface ServiceAccount {
    id: number;
    guid: string;
    is_protected: boolean;
    protected_mode: number;
    smart_policy_name?: string;
    type_resolved: string;
    is_gmsa: boolean;
    risk: number;
    baseline_change?: number;
    auths_count: number;
    comment: string;
    last_use?: number;
    display_name: string;
    calculated_name?: string;
    domain_name: string;
    devices: number;
    resources: number;
    is_domain_admin: boolean;
    is_internal_sa: boolean;
    remove_reason: string | null;
    identifier?: string;
    last_rotation?: number;
    account_owners?: ADOwnerData[];
    custom_owners?: CustomOwnerData[];
}

export type ReportType = 'currentFilter' | 'resourcesReport' | 'pamReport';

export enum ENTITY_IDENTIFIER_TYPES {
    IP = 'ip',
    NAME = 'name',
    GUID = 'guid'
}

type IdentifierType = ENTITY_IDENTIFIER_TYPES.IP | ENTITY_IDENTIFIER_TYPES.NAME | ENTITY_IDENTIFIER_TYPES.GUID;

export interface ServiceAccountEntity {
    name?: string;
    identifier: string;
    identifier_type: IdentifierType;
    type?: number;
    last_use?: number;
    path?: string;
    hits?: number;
    domain_name?: string;
    is_allowed: boolean;
    is_disabled?: boolean;
}

export interface ServiceAccountAccountOwnerData {
    name: string;
    domain: string;
}

export enum RiskRestrictionTypes {
    RISK_LEVEL = 'risk_level',
    RISK_INDICATORS = 'risk_indicators'
}

export interface ServiceAccountData {
    id: number;
    guid: string;
    display_name: string;
    identifier: string;
    domain_name: string;
    pwd_last_set?: number;
    identity_store: string;
    is_protected: boolean;
    protected_mode: number;
    type_resolved: string;
    is_gmsa: boolean;
    is_internal_sa: boolean;
    risk: number;
    comment: string;
    application: string;
    account_owners?: ADOwnerData[];
    customOwners: CustomOwnerData[];
    remove_reason?: string;
    protected_protocols: string[];
    last_use?: number;
    last_use_kerberos?: number;
    last_use_ntlm?: number;
    last_use_ldap: number;
    last_use_ldaps: number;
    last_use_ldap_ldaps?: number;
    devices: ServiceAccountEntity[];
    resources: ServiceAccountEntity[];
    groups: { name: string; guid: string }[];
    restrict_resources: boolean;
    restrict_devices: boolean;
    restrict_risk: boolean;
    risk_restriction_type: RiskRestrictionTypes;
    protect_risk_indicators: string[];
    protection_risk_level: number;
    actions: ServiceAccountAction[];
    ui_remove_reason: string | null;
    custom_ui_remove_reason: string | null;
    ui_remove_details: string | null;
}

export interface ServiceAccountTab {
    name: ReactNode;
    value: number;
    type: string;
    description: string;
    showRemovedAccounts: boolean;
    amount: number;
    selectedServiceAccounts: ServiceAccount[];
}

export interface ServiceAccountFilters {
    display_name?: string;
    account_type?: string[];
    risk?: number[];
    is_protected?: string[];
    actions?: string[];
    domain_name?: string[];
    baseline_change?: [string, number];
    owner?: string[];
    custom_owner?: string[];
    insights?: string[];
    showRemovedAccounts?: string;
    type_resolved?: string;
    smart_policies?: string[];
    send_to_siem?: string[];
}

export type ColumnFilter = FilterVariantProps<string | number | boolean> & {
    id: keyof ServiceAccountFilters;
    showFilter: boolean;
};

export type ReportsMessages = { id: number; key: ReportType; isJson: boolean; isLoading: boolean };

export interface ServiceAccountInsight {
    title: string;
    filters?: ServiceAccountFilters;
    count: number;
    main?: boolean;
    disabled?: boolean;
    info: string;
}

export type ServiceAccountExportOptions = {
    [key: string]: {
        title: string;
        reportName: string;
        downloadTitle: string;
        tooltip?: string;
        sendFilter?: boolean;
        filePrefix: string;
        filters: {
            csv: {
                predefined: null | { removedAccounts: { exclude: boolean }; ignoreTypeFilter: boolean };
                sendFilter: boolean;
                entitiesFilter: { includeResourcesData: boolean; isAllowed?: boolean };
            };
            json: {
                predefined: null;
                sendFilter: boolean;
                entitiesFilter: null;
            };
        };
        supportedReportTypes: {
            csv: boolean;
            json: boolean;
        };
        csvData?: {
            fileName: string;
            fileCreationTime: string;
        };
        jsonData?: {
            fileName: string;
            fileCreationTime: string;
        };
    };
};

export type OwnerFullData = {
    name: string;
    domain: string;
    is_disabled: boolean;
    guid: string;
    path: string;
    added_by: OwnersAddedBy;
};

export enum CustomOwnerType {
    EMAIL = 'email',
    CUSTOM_TEXT = 'custom'
}

export enum AdOwnerType {
    GUID = 'guid'
}

export enum OwnersAddedBy {
    API = 'api',
    UI = 'ui',
    AD_SYNCED = 'ad_synced'
}

export type ADOwnerData = {
    owner_guid: string;
    added_by: OwnersAddedBy;
    user_display_name?: string;
    group_display_name?: string;
};

export type CustomOwnerData = {
    owner_type: CustomOwnerType;
    owner_value: string;
    added_by: OwnersAddedBy;
};

export type SyncStatus = 'success' | 'fail' | 'loading';
